import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearchPlus, 
  faTimes, 
  faRuler, 
  faChild, 
  faUsers, 
  faWeight, 
  faBolt, 
  faTruck, 
  faArrowRight 
} from '@fortawesome/free-solid-svg-icons';
import PlaceholderImage from '../common/PlaceholderImage';
import { getAllProductsFilteredByLocation } from '../../services/productService';
import { getCategories } from '../../services/categoryService';
import { useLocation } from '../../context/LocationContext';
import '../../styles/rentals.css';

const FeaturedRentals = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedItem, setExpandedItem] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredRentals, setFilteredRentals] = useState([]);
  const { currentLocation } = useLocation();

  // Helper function to get first sentence of a description
  const getShortDescription = (description) => {
    if (!description) return '';
    
    // Match first sentence (ending with period, exclamation, question mark)
    const match = description.match(/^(.*?[.!?])\s/);
    
    if (match) {
      // Return first sentence + ellipsis
      return match[1] + '...';
    } else if (description.length > 100) {
      // If no sentence found, truncate at 100 chars
      return description.substring(0, 100).trim() + '...';
    } else {
      // If description is already short enough, return as is
      return description;
    }
  };

  // Fetch products from Firebase based on current location
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch products filtered by current location
        const productsData = await getAllProductsFilteredByLocation(currentLocation);
        setProducts(productsData);
        
        // Fetch categories from Firebase
        const categoriesData = await getCategories();
        setCategories(categoriesData);
        
        // Convert Firebase products to the format expected by the UI
        const formattedProducts = productsData
          .filter(product => product.popular) // Only show popular products
          .map(product => ({
            id: product.id,
            name: product.name,
            price: `$${product.price}/day`,
            description: product.description,
            shortDescription: product.shortDescription || getShortDescription(product.description), // Use shortDescription if exists, otherwise generate one
            dimensions: product.dimensions || '',
            ageRange: product.ageRange || '',
            capacity: product.capacity || '',
            image: product.images && product.images.length > 0 ? product.images[0].url : '',
            category: product.categoryId,
            popular: product.popular || false,
            location: product.location || currentLocation
          }));
        
        setFilteredRentals(formattedProducts);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentLocation]); // Re-fetch when location changes

  // Update filtered rentals when filter changes
  useEffect(() => {
    if (!products.length) return;
    
    let filtered = [...products];
    
    // Filter by category if not 'all'
    if (activeFilter !== 'all') {
      const categoryId = categories.find(cat => cat.name.toLowerCase() === activeFilter.toLowerCase())?.id;
      
      if (categoryId) {
        filtered = filtered.filter(product => product.categoryId === categoryId);
      }
    }
    
    // Only show popular products
    filtered = filtered.filter(product => product.popular);
    
    // Format products for display
    const formattedProducts = filtered.map(product => ({
      id: product.id,
      name: product.name,
      price: `$${product.price}/day`,
      description: product.description,
      shortDescription: product.shortDescription || getShortDescription(product.description), // Use shortDescription if exists, otherwise generate one
      dimensions: product.dimensions || '',
      ageRange: product.ageRange || '',
      capacity: product.capacity || '',
      image: product.images && product.images.length > 0 ? product.images[0].url : '',
      category: product.categoryId,
      popular: product.popular || false,
      location: product.location || currentLocation
    }));
    
    setFilteredRentals(formattedProducts);
  }, [activeFilter, products, categories, currentLocation]);

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const handleQuickView = (id) => {
    setExpandedItem(id);
    document.body.style.overflow = 'hidden';
  };

  const closeExpandedInfo = () => {
    setExpandedItem(null);
    document.body.style.overflow = 'auto';
  };

  // Get category names for filters
  const getCategoryFilters = () => {
    // Extract unique category names to create filter buttons
    if (!categories.length) return [];
    
    // Get categories that have popular products in current location
    const popularCategoryIds = new Set(
      products
        .filter(product => product.popular)
        .map(product => product.categoryId)
    );
    
    // Return categories that have popular products
    return categories.filter(category => popularCategoryIds.has(category.id));
  };

  return (
    <section className="featured-rentals" id="featured-rentals">
      <div className="featured-rentals-container">
        <h2>Check Out Our Most Popular Rentals in {currentLocation}!</h2>
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterClick('all')}
          >
            All Rentals
          </button>
          {getCategoryFilters().map(category => (
            <button
              key={category.id}
              className={`filter-btn ${activeFilter === category.name.toLowerCase() ? 'active' : ''}`}
              onClick={() => handleFilterClick(category.name.toLowerCase())}
            >
              {category.name}
            </button>
          ))}
        </div>
        
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading featured rentals...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
          </div>
        ) : filteredRentals.length === 0 ? (
          <div className="no-featured-rentals">
            <p>No featured rentals available in {currentLocation} right now.</p>
            <p>Check our full catalog or switch locations to see more options!</p>
          </div>
        ) : (
          <div className="featured-rentals-grid">
            {filteredRentals.map(item => (
              <div className="rental-card" key={item.id}>
                <div className="rental-image">
                  {item.image ? (
                    <img src={item.image} alt={item.name} />
                  ) : (
                    <PlaceholderImage alt={item.name} />
                  )}
                  <Link to={`/rentals/${item.id}`} className="view-details">
                    View Details
                  </Link>
                </div>
                <div className="rental-info">
                  <h4>{item.name}</h4>
                  <p className="price">{item.price}</p>
                  <p className="short-description">{item.shortDescription}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="view-all-container">
          <Link to="/rentals" className="view-all-button">
            View All Rentals in {currentLocation} <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedRentals;
